import { z } from 'zod';
/*
  HOW A CONSULTANT NAMES A GROUP OF PEOPLE, as a schema.

  WHY THIS EXISTS AT ALL, and it is the arithmetic rather than a preference. `redact.ts` caps what may
  be described to the model at 500 people, because a carve-out roster is routinely thousands and a
  request carrying all of them does not fit. Above that cap the model is handed a SHAPE — level names
  and counts, no identifiers — so "move Finance to the carve-out" cannot be acted on: there is nothing
  in the model's context to name. It asks for employee ids, which the person asking does not have
  either, and the conversation deadlocks.

  `redact.ts:131` states the answer and this is it: "an agent that ASKS for the people matching a
  filter, not one handed a population; the demo sentence — everyone in Finance with dedication above
  60% — is itself a filter."

  IN CONTRACTS RATHER THAN IN THE AGENT, for the reason every command schema is: the tool definition
  the model reads is GENERATED from this, so the vocabulary the model is offered and the vocabulary
  the server enforces cannot drift apart. One schema, two readers.

  EVERY FIELD IS OPTIONAL AND THEY COMBINE WITH AND. A filter naming nothing matches everybody, which
  is a legitimate request ("how many people are there") and a dangerous one to act on blindly — the
  bound on what comes back is what makes it safe, not a refusal to answer.
*/
/**
 * The salary field is deliberately absent, and its absence is the point.
 *
 * `FIELD_POLICY` sends salary as a RANK rather than a number, so a filter on it would have to be a
 * filter on the rank — and "everyone earning over 90,000" answered against a rank is a different
 * question wearing the same words. Dedication, seniority and the grouping levels are what a
 * separation is actually reasoned about; salary belongs to the analytics panes, where the person
 * looking at it has the numbers in front of them.
 */
export const peopleFilterSchema = z.object({
    /**
     * A grouping level and the value to match in it — "lvl2" and "Finance".
     *
     * The level KEY rather than a guess at which tier a name belongs to: the hierarchy is per project,
     * a value can repeat across tiers, and the summary the model is given lists the keys beside their
     * values for exactly this reason.
     */
    level: z.string().min(1).max(60).optional(),
    levelValue: z.string().min(1).max(200).optional(),
    /** Free-text contains, case-insensitive. For "the engineers", where no level says so. */
    jobTitleContains: z.string().min(1).max(200).optional(),
    location: z.string().min(1).max(200).optional(),
    country: z.string().min(1).max(200).optional(),
    seniority: z.string().min(1).max(200).optional(),
    businessUnit: z.string().min(1).max(200).optional(),
    legalEntity: z.string().min(1).max(200).optional(),
    /** 0..1, inclusive. "above 60% dedicated" is `dedicationAtLeast: 0.6`. */
    dedicationAtLeast: z.number().min(0).max(1).optional(),
    dedicationAtMost: z.number().min(0).max(1).optional(),
    /**
     * Which side of the separation they are on RIGHT NOW, so a request can name the population it is
     * about rather than the one it is creating — "the people still in RemainCo who serve the carve-out".
     */
    bucket: z.enum(['Carve-out', 'Remain', 'Undecided']).optional(),
    /**
     * Whether to include people every aggregate in this product excludes.
     *
     * Off by default and stated rather than implied: leavers and inactive rows are in the roster and
     * out of every number, so a filter that silently included them would answer a different question
     * from the one the panes on screen are answering, and the two would disagree with no way to see why.
     */
    includeInactive: z.boolean().default(false),
    includeOpenPositions: z.boolean().default(true),
});
/**
 * How many identifiers one answer may carry.
 *
 * SMALLER THAN THE ROSTER CAP, deliberately. 500 is what a whole request can hold; a tool RESULT
 * shares that budget with the system prompt, the tool catalogue, the conversation so far and the
 * answer still to come, so a result allowed to fill the window on its own would push out the context
 * that makes it useful. 200 is a large team and a small department.
 *
 * A filter matching more is not refused — the COUNT is always exact and always returned. What is
 * bounded is the enumeration, and saying "412 people match, here are the first 200" is the honest
 * shape: it lets the model narrow, and it never silently acts on a third of a department.
 */
export const MAX_FOUND_PEOPLE = 200;
//# sourceMappingURL=people-filter.js.map